import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';
import { useToast } from '@/components/feature/ToastProvider';
import type { Work } from '@/mocks/catalog';
import { typeLabel } from '@/mocks/catalog';

export default function WorkHero({ work }: { work: Work }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const saved = isFavorite(work.id);

  const handleFavorite = () => {
    if (saved) {
      setConfirmOpen(true);
      return;
    }
    toggleFavorite(work.id);
    showToast(`已将《${work.title}》加入我的收藏`);
  };

  const removeFavorite = () => {
    toggleFavorite(work.id);
    setConfirmOpen(false);
    showToast(`已从收藏中移除《${work.title}》`, 'info');
  };

  return (
    <>
      <div className="relative w-full overflow-hidden">
        <div className="absolute inset-0 h-full w-full">
          <img
            src={work.backdrop}
            alt={`${work.title} 剧照`}
            title={`${work.title} - 91大神影视详情`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/85 to-foreground-950/60"></div>
        </div>

        <div className="relative mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-4 py-8 md:px-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-white/55" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-white/30"></i>
            <Link to={`/category/${work.type}`} className="transition-colors hover:text-accent-300">
              {typeLabel[work.type]}
            </Link>
            <i className="ri-arrow-right-s-line text-white/30"></i>
            <span className="text-white/80">{work.title}</span>
          </nav>

          <div className="flex flex-col gap-6 sm:flex-row sm:items-start md:gap-8">
            <div className="w-[150px] shrink-0 overflow-hidden rounded-lg border border-white/15 sm:w-[190px] md:w-[220px]">
              <div className="aspect-[3/4] w-full bg-primary-900">
                <img
                  src={work.poster}
                  alt={`${work.title} 海报`}
                  title={`${work.title} - ${work.genres.join(' ')}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 font-label text-[11px] font-bold text-foreground-950">
                  {typeLabel[work.type]}
                </span>
                {work.badge && (
                  <span className="rounded-md border border-white/25 px-2 py-0.5 text-[11px] text-white/85">{work.badge}</span>
                )}
                <span className="flex items-center gap-1 rounded-md bg-white/10 px-2 py-0.5 font-label text-[11px] text-white/85">
                  <i className="ri-fire-line text-accent-400"></i>
                  {work.views} 次关注
                </span>
              </div>

              <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-white md:text-4xl">
                {work.title}
              </h1>
              <p className="mt-1 font-label text-xs uppercase tracking-wider text-white/50">{work.titleEn}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/75 md:text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-accent-400"></i>
                  {work.year}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line text-accent-400"></i>
                  {work.region}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-accent-400"></i>
                  {work.duration}
                </span>
                <span className="flex items-center gap-1 font-label font-bold text-accent-400">
                  <i className="ri-star-fill"></i>
                  {work.rating.toFixed(1)} 分
                </span>
              </div>

              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/75">{work.synopsis}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-white/60">
                <span className="text-white/45">导演</span>
                <span className="text-white/85">{work.director}</span>
                <span className="text-white/20">|</span>
                <span className="text-white/45">主演</span>
                <span className="text-white/85">{work.cast.join('、')}</span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors ${
                    saved
                      ? 'bg-background-50 text-foreground-950 hover:bg-background-100'
                      : 'bg-accent-500 text-foreground-950 hover:bg-accent-400'
                  }`}
                >
                  <i className={saved ? 'ri-heart-fill text-accent-600' : 'ri-heart-3-line'}></i>
                  {saved ? '已收藏' : '加入收藏'}
                </button>
                <Link
                  to={`/category/${work.type}/list?genre=${encodeURIComponent(work.genres[0])}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/25 bg-white/10 px-4 py-2.5 text-sm text-white transition-colors hover:bg-white/20"
                >
                  <i className="ri-layout-grid-line"></i>
                  更多{work.genres[0]}作品
                </Link>
                <Link
                  to="/favorites"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:text-white"
                >
                  <i className="ri-play-list-add-line"></i>
                  我的片单
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/60 px-4 animate-fade-in">
          <div className="w-full max-w-sm rounded-lg border border-background-200/70 bg-background-50 p-5">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-heart-3-line"></i>
              </span>
              <div>
                <h2 className="font-display text-base font-bold text-foreground-950">从收藏中移除？</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">
                  《{work.title}》已经在你的本地片单中，确认要移除吗？
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={removeFavorite}
                className="whitespace-nowrap rounded-md bg-primary-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                确认移除
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}